/* ============================================================================
 * ez-engine-shade.js — EZ-Snap SHADE configurator engine (theme asset, vanilla JS)
 * Source of truth: demo/app/lib/shadePricing.js (round-2 shade model).
 *
 * PORTED PURE MATH (no framework, no imports): the geometric snap rule, the
 * smallest-pack rule, the least-cost snap-pack DP, the roll-length plan, and the
 * precut-vs-roll compare math. PRICE / VARIANT DATA IS NOT BAKED IN — the engine is
 * a factory that RECEIVES a catalog object built by Liquid at render:
 *
 *   const shade = window.EZSnap.createShadeEngine({
 *     precut: [{ w, h, sku, price, variantId }, ...],   // the 20 precut sizes
 *     rolls:  [{ ft, sku, price, variantId,
 *                snaps:{ light?, medium, heavy } }, ...], // stock roll lengths
 *     snaPacks: [{ n, price, sku?, variantId? }, ...],   // SNA snap packs (loose)
 *     packSizes: [15,25,40,50],  // OPTIONAL — precut-bundle pack ladder (default below)
 *     spares: 1.05               // OPTIONAL — snap spares buffer (default below)
 *   });
 *
 * PUBLIC API (all pure — no DOM, no network):
 *   shade.precutSnaps(w, h)            -> geometric snap count for a W×H panel (10" rule)
 *   shade.packForSnaps(snaps[, sizes]) -> smallest precut pack >= ceil(snaps*spares)
 *   shade.precutPackSize(w, h)         -> the SNA pack a precut bundles
 *   shade.coverSnaps(total[, packs])   -> least-cost [{n,count}] SNA packs covering >= total
 *   shade.coverCost(total[, packs])    -> $ of that least-cost cover (needs catalog prices)
 *   shade.rollPlan(consumedFt)         -> [ft,...] stock rolls (single <=25 else least-surplus)
 *   shade.fabricSize(openW, openH, perSide) -> { w, h } fabric size for an opening
 *   shade.precutMatch(needW, needH)    -> smallest precut catalog entry covering need, or null
 *   shade.precutOversize(needW, needH) -> true when no precut can cover the need
 *   shade.precutLine(w, h)             -> { sku, price, variantId, snaps, pack } for a precut buy
 *   shade.rollLine(ft, tier)           -> { roll:{...}, snaps, packs:[{n,count,...}], price }
 *   shade.comparePrecutRoll(windows)   -> { precut:{...}, roll:{...}, cheaper } compare math
 *
 * `packs`/`sizes` default to the injected catalog; every price in a returned line
 * comes from the injected catalog, never a literal in this file.
 * ============================================================================ */
(function (root, factory) {
  var api = factory();
  if (typeof module !== "undefined" && module.exports) module.exports = api; // node/gate
  if (root && typeof root === "object") {
    root.EZSnap = root.EZSnap || {};
    root.EZSnap.createShadeEngine = api.createShadeEngine;
  }
})(typeof window !== "undefined" ? window : this, function () {
  "use strict";

  var DEFAULT_PACK_SIZES = [15, 25, 40, 50]; // precut-bundle pack ladder (§2B)
  var DEFAULT_SPARES = 1.05; // +5% snap spares buffer (matches skirting)

  // Geometric snap count for a W×H fabric panel — the documented "10-inch rule" (design doc §2B /
  // BOM PDF section 08), which the precut SKU sheets match exactly (25/25 sizes). Per-side CEILING at
  // the spacing, plus 2 snaps at each corner (the +4). Spacing tier: >24 sq ft -> 5" (denser, large
  // adhesive panels), else 10". Troy (fulfillment) states the boundary as 25 sq ft; we keep 24 (owner
  // decision 2026-08-11) so the change is a PURE INCREASE -- no order ever ships fewer than before.
  // Sizes the SNA snap packs + SNS studs too, so all three track. Even by construction.
  //   Changed 2026-08-11: was a port of the old Woo calc's perimeter-round (round(2(w+h)/s+4)), which
  //   under-counted the documented rule by ~2/window; the precut bundles ship this rule, so the roll
  //   path now matches them. Reconciled over 3,831 real quotes: +4.6% snaps shipped, 79% of orders
  //   unchanged, 0 ship fewer.
  function precutSnaps(w, h) {
    var spacing = (w / 12) * (h / 12) > 24 ? 5 : 10; // sq ft; >24 -> 5" (dense, large), else 10"
    return 2 * Math.ceil(w / spacing) + 2 * Math.ceil(h / spacing) + 4; // per-side ceiling + 2 per corner
  }

  // Smallest pack >= ceil(snaps * spares) from a ladder.
  function packForSnaps(snaps, sizes, spares) {
    sizes = sizes || DEFAULT_PACK_SIZES;
    spares = spares == null ? DEFAULT_SPARES : spares;
    var target = Math.ceil(snaps * spares);
    var hit = sizes.find(function (s) { return s >= target; });
    return hit != null ? hit : sizes[sizes.length - 1];
  }

  // Cover a snap count with SNA packs at LEAST cost — a DP over pack prices, not greedy.
  // packs: [{ n, price }] (price == the catalog msrp). dp[r] = min cost to cover >= r.
  // Returns [{ n, count }] sorted by n asc.
  function coverSnaps(total, packs) {
    var target = Math.ceil(total);
    if (target <= 0) return [];
    var opts = (packs || []).filter(function (p) { return p.price != null; });
    if (!opts.length) return [];
    var dp = new Array(target + 1).fill(Infinity);
    var pick = new Array(target + 1).fill(-1);
    dp[0] = 0;
    for (var r = 1; r <= target; r++) {
      for (var i = 0; i < opts.length; i++) {
        var p = opts[i];
        var cost = dp[Math.max(0, r - p.n)] + p.price;
        if (cost < dp[r]) { dp[r] = cost; pick[r] = p.n; }
      }
    }
    var chosen = {};
    for (var q = target; q > 0; ) {
      var n = pick[q];
      if (n < 0) break; // safety (no pack reaches q) — shouldn't happen with a finite ladder
      chosen[n] = (chosen[n] || 0) + 1;
      q = Math.max(0, q - n);
    }
    return Object.keys(chosen).map(function (k) {
      return { n: Number(k), count: chosen[k] };
    }).sort(function (a, b) { return a.n - b.n; });
  }

  // Cover a snap count with the FEWEST total pieces (min overage) — price-BLIND, so the result is a
  // deterministic tight cover independent of the (possibly non-monotonic) pack prices. Used to size the
  // body-color STUDS so their count tracks the snap count instead of chasing least-cost packs.
  // packs: [{ n }]. dp[r] = min pieces to cover >= r. Returns [{ n, count }] sorted by n asc.
  function coverTight(total, packs) {
    var target = Math.ceil(total);
    if (target <= 0) return [];
    var opts = (packs || []).filter(function (p) { return p.n > 0; });
    if (!opts.length) return [];
    var dp = new Array(target + 1).fill(Infinity);
    var pick = new Array(target + 1).fill(-1);
    dp[0] = 0;
    for (var r = 1; r <= target; r++) {
      for (var i = 0; i < opts.length; i++) {
        var p = opts[i];
        var pieces = dp[Math.max(0, r - p.n)] + p.n; // minimize total pieces, not cost
        if (pieces < dp[r]) { dp[r] = pieces; pick[r] = p.n; }
      }
    }
    var chosen = {};
    for (var q = target; q > 0; ) {
      var n = pick[q];
      if (n < 0) break;
      chosen[n] = (chosen[n] || 0) + 1;
      q = Math.max(0, q - n);
    }
    return Object.keys(chosen).map(function (k) {
      return { n: Number(k), count: chosen[k] };
    }).sort(function (a, b) { return a.n - b.n; });
  }

  // Per-side allowance → fabric size needed for an opening (config-req §5.0).
  function fabricSize(openW, openH, perSide) {
    return { w: openW + 2 * perSide, h: openH + 2 * perSide };
  }

  function createShadeEngine(catalog) {
    catalog = catalog || {};
    var PRECUT = (catalog.precut || []).slice();
    var ROLLS = (catalog.rolls || []).slice().sort(function (a, b) { return a.ft - b.ft; });
    var SNA = (catalog.snaPacks || []).slice();
    var PACK_SIZES = catalog.packSizes || DEFAULT_PACK_SIZES;
    var SPARES = catalog.spares == null ? DEFAULT_SPARES : catalog.spares;
    var ROLL_LENGTHS = ROLLS.map(function (r) { return r.ft; });

    function precutMatch(needW, needH) {
      var fits = function (p) {
        return (p.w >= needW && p.h >= needH) || (p.h >= needW && p.w >= needH);
      };
      return PRECUT.filter(fits).sort(function (a, b) {
        return a.w * a.h - b.w * b.h;
      })[0] || null;
    }
    function precutOversize(needW, needH) { return precutMatch(needW, needH) == null; }

    // Roll plan for a consumed linear length (ft). <=maxStock → smallest single roll >= need;
    // else the least-surplus combination of stock lengths (prefer fewer rolls).
    function rollPlan(consumedFt) {
      if (consumedFt <= 0) return [];
      var STOCK = ROLL_LENGTHS;
      var maxStock = STOCK[STOCK.length - 1];
      if (consumedFt <= maxStock) {
        return [STOCK.find(function (ft) { return ft >= consumedFt; })];
      }
      var best = null;
      var search = function (remaining, chosen, total) {
        if (remaining <= 0) {
          var surplus = total - consumedFt;
          if (!best || surplus < best.surplus ||
              (surplus === best.surplus && chosen.length < best.rolls.length)) {
            best = { rolls: chosen.slice(), surplus: surplus };
          }
          return;
        }
        if (chosen.length >= 12) return; // safety bound
        for (var i = 0; i < STOCK.length; i++) {
          var ft = STOCK[i];
          if (chosen.length && ft > chosen[chosen.length - 1]) continue; // non-increasing → dedupe
          search(remaining - ft, chosen.concat([ft]), total + ft);
        }
      };
      search(consumedFt, [], 0);
      return best ? best.rolls : [maxStock];
    }

    var precutPackSize = function (w, h) {
      return packForSnaps(precutSnaps(w, h), PACK_SIZES, SPARES);
    };

    var coverCost = function (total, packs) {
      packs = packs || SNA;
      return coverSnaps(total, packs).reduce(function (s, c) {
        var pk = packs.find(function (p) { return p.n === c.n; }) || {};
        return s + (pk.price || 0) * c.count;
      }, 0);
    };

    // A single precut purchase line (catalog-priced).
    function precutLine(w, h) {
      var p = PRECUT.find(function (x) { return x.w === w && x.h === h; });
      if (!p) return null;
      var snaps = precutSnaps(w, h);
      return {
        w: w, h: h, sku: p.sku, price: p.price, variantId: p.variantId,
        snaps: snaps, pack: precutPackSize(w, h),
      };
    }

    // A roll purchase line at a snap-density tier (light|medium|heavy). Snaps are covered
    // by least-cost SNA packs; price = roll + packs (all from the injected catalog).
    function rollLine(ft, tier) {
      tier = tier || "medium";
      var roll = ROLLS.find(function (r) { return r.ft === ft; });
      if (!roll) return null;
      var snapCount = (roll.snaps && (roll.snaps[tier] != null ? roll.snaps[tier] : roll.snaps.medium)) || 0;
      var packs = coverSnaps(snapCount, SNA).map(function (c) {
        var pk = SNA.find(function (p) { return p.n === c.n; }) || {};
        return { n: c.n, count: c.count, sku: pk.sku, price: pk.price, variantId: pk.variantId };
      });
      var packTotal = packs.reduce(function (s, p) { return s + (p.price || 0) * p.count; }, 0);
      return {
        ft: ft, tier: tier, sku: roll.sku, variantId: roll.variantId,
        rollPrice: roll.price, snaps: snapCount,
        delivered: packs.reduce(function (s, p) { return s + p.n * p.count; }, 0),
        packs: packs,
        price: (roll.price || 0) + packTotal,
      };
    }

    // Precut-vs-roll compare for a window list. Each window: { openW, openH, perSide }
    // (perSide already resolved from the app/type upstream). Precut path buys the closest
    // precut per window (bundle-priced, snaps included). Roll path nests → consumed ft →
    // rollPlan → least-cost snaps for the total geometric snap count.
    function comparePrecutRoll(windows, nestFn) {
      // ---- precut path ----
      var precutLines = [];
      var precutTotal = 0;
      var precutOK = true;
      windows.forEach(function (win) {
        var fab = fabricSize(win.openW, win.openH, win.perSide || 0);
        var m = precutMatch(fab.w, fab.h);
        if (!m) { precutOK = false; return; }
        var line = precutLine(m.w, m.h);
        precutLines.push(line);
        precutTotal += line.price || 0;
      });
      // ---- roll path ----
      var panels = windows.map(function (win) {
        var fab = fabricSize(win.openW, win.openH, win.perSide || 0);
        return { fabW: fab.w, fabH: fab.h, winW: win.openW, winH: win.openH };
      });
      var nest = nestFn ? nestFn(panels) : null;
      var consumedFt = nest ? nest.lengthFt : 0;
      var rolls = rollPlan(consumedFt);
      var rollCost = rolls.reduce(function (s, ft) {
        var r = ROLLS.find(function (x) { return x.ft === ft; }) || {};
        return s + (r.price || 0);
      }, 0);
      // total geometric snaps across all fabric panels (covered once, least-cost)
      var totalSnaps = panels.reduce(function (s, p) {
        return s + precutSnaps(p.fabW, p.fabH);
      }, 0);
      var rollSnapPacks = coverSnaps(totalSnaps, SNA).map(function (c) {
        var pk = SNA.find(function (p) { return p.n === c.n; }) || {};
        return { n: c.n, count: c.count, sku: pk.sku, price: pk.price };
      });
      var rollSnapCost = rollSnapPacks.reduce(function (s, p) { return s + (p.price || 0) * p.count; }, 0);
      var rollTotal = rollCost + rollSnapCost;

      return {
        precut: { ok: precutOK, lines: precutLines, total: precutOK ? precutTotal : null },
        roll: {
          rolls: rolls, consumedFt: consumedFt, snaps: totalSnaps,
          snapPacks: rollSnapPacks, rollCost: rollCost, snapCost: rollSnapCost, total: rollTotal,
          layout: nest ? nest.placed : null,
        },
        cheaper: !precutOK ? "roll" : (precutTotal <= rollTotal ? "precut" : "roll"),
      };
    }

    return {
      // pure math (also exposed statically below)
      precutSnaps: precutSnaps,
      packForSnaps: function (snaps, sizes) { return packForSnaps(snaps, sizes || PACK_SIZES, SPARES); },
      precutPackSize: precutPackSize,
      coverSnaps: function (total, packs) { return coverSnaps(total, packs || SNA); },
      coverTight: function (total, packs) { return coverTight(total, packs || SNA); },
      coverCost: coverCost,
      fabricSize: fabricSize,
      rollPlan: rollPlan,
      // catalog-aware
      precutMatch: precutMatch,
      precutOversize: precutOversize,
      precutLine: precutLine,
      rollLine: rollLine,
      comparePrecutRoll: comparePrecutRoll,
      // catalog accessors
      catalog: { precut: PRECUT, rolls: ROLLS, snaPacks: SNA, packSizes: PACK_SIZES, spares: SPARES },
    };
  }

  return {
    createShadeEngine: createShadeEngine,
    // static pure helpers (catalog-free) for the gate + any callers
    precutSnaps: precutSnaps,
    packForSnaps: packForSnaps,
    coverSnaps: coverSnaps,
    coverTight: coverTight,
    fabricSize: fabricSize,
  };
});
